import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { isSupabaseConfigured, supabase } from "@/lib/supabase";
import { useAuth } from "@/lib/auth";
import type {
  GradeFormValues,
  MaterialFormValues,
  ShapeSizeFormValues,
  UomFormValues,
} from "@/features/materials/schema";
import type {
  Material,
  MaterialGrade,
  MaterialListRow,
  ShapeSize,
  ShapeSizeListRow,
  Uom,
} from "@/features/materials/types";

const text = (value: string | undefined) => value?.trim() || null;

function numOrNull(value: number | null | undefined) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return null;
  return Number(value);
}

export function materialToForm(m: Material): MaterialFormValues {
  return {
    name: m.name,
    category: m.category,
    requires_shape_size: m.requires_shape_size,
    uom_id: m.uom_id,
    description: m.description ?? "",
    batch_prefix: m.batch_prefix,
    parent_material_id: m.parent_material_id ?? "",
    is_active: m.is_active,
  };
}

export function useUoms() {
  return useQuery({
    queryKey: ["uoms"],
    enabled: isSupabaseConfigured,
    queryFn: async (): Promise<Uom[]> => {
      const { data, error } = await supabase.from("uoms").select("*").order("code");
      if (error) throw error;
      return (data ?? []) as Uom[];
    },
  });
}

export function useMaterials() {
  return useQuery({
    queryKey: ["materials"],
    enabled: isSupabaseConfigured,
    queryFn: async (): Promise<MaterialListRow[]> => {
      const { data, error } = await supabase
        .from("materials")
        .select("*, uom:uoms(id, code, name), parent:materials!parent_material_id(id, name)")
        .order("name");
      if (error) throw error;
      return (data ?? []) as MaterialListRow[];
    },
  });
}

export function useMaterial(id?: string) {
  return useQuery({
    queryKey: ["materials", id],
    enabled: isSupabaseConfigured && Boolean(id),
    queryFn: async (): Promise<Material> => {
      const { data, error } = await supabase.from("materials").select("*").eq("id", id!).single();
      if (error) throw error;
      return data as Material;
    },
  });
}

export function useMaterialGrades() {
  return useQuery({
    queryKey: ["material_grades"],
    enabled: isSupabaseConfigured,
    queryFn: async (): Promise<MaterialGrade[]> => {
      const { data, error } = await supabase.from("material_grades").select("*").order("name");
      if (error) throw error;
      return (data ?? []) as MaterialGrade[];
    },
  });
}

export function useShapeSizes(materialId?: string) {
  return useQuery({
    queryKey: ["shape_sizes", materialId ?? "all"],
    enabled: isSupabaseConfigured,
    queryFn: async (): Promise<ShapeSizeListRow[]> => {
      // Avoid self-embed on shape_sizes — PostgREST schema cache often misses
      // the mother_size_id self-FK. Resolve mother from the fetched rows instead.
      let q = supabase
        .from("shape_sizes")
        .select("*, material:materials!applicable_material_id(id, name)")
        .order("shape", { ascending: true })
        .order("size_label", { ascending: true });
      if (materialId) q = q.eq("applicable_material_id", materialId);
      const { data, error } = await q;
      if (error) throw error;

      const rows = (data ?? []) as ShapeSizeListRow[];
      const byId = new Map(rows.map((r) => [r.id, r]));

      // Mothers may be outside the material filter (rare) — fetch missing ids
      const missingMotherIds = [
        ...new Set(
          rows
            .map((r) => r.mother_size_id)
            .filter((id): id is string => Boolean(id) && !byId.has(id as string)),
        ),
      ];
      if (missingMotherIds.length > 0) {
        const { data: mothers, error: motherError } = await supabase
          .from("shape_sizes")
          .select("id, shape, size_label, od")
          .in("id", missingMotherIds);
        if (motherError) throw motherError;
        for (const m of mothers ?? []) {
          byId.set(m.id, m as ShapeSizeListRow);
        }
      }

      return rows.map((row) => {
        const motherRow = row.mother_size_id ? byId.get(row.mother_size_id) : undefined;
        return {
          ...row,
          mother: motherRow
            ? {
                id: motherRow.id,
                shape: motherRow.shape,
                size_label: motherRow.size_label,
              }
            : null,
        };
      });
    },
  });
}

export function useCreateMaterial() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (values: MaterialFormValues) => {
      const payload = {
        name: values.name.trim(),
        category: values.category,
        requires_shape_size: values.requires_shape_size,
        uom_id: values.uom_id,
        description: text(values.description),
        batch_prefix: values.batch_prefix.trim().toUpperCase(),
        parent_material_id: values.parent_material_id?.trim() || null,
        is_active: values.is_active,
        created_by: user?.id ?? null,
        updated_by: user?.id ?? null,
      };
      const { data, error } = await supabase.from("materials").insert(payload).select("*").single();
      if (error) throw error;
      return data as Material;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["materials"] });
      toast.success("Material created");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to create material"),
  });
}

export function useUpdateMaterial() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ id, values }: { id: string; values: MaterialFormValues }) => {
      const payload = {
        name: values.name.trim(),
        category: values.category,
        requires_shape_size: values.requires_shape_size,
        uom_id: values.uom_id,
        description: text(values.description),
        batch_prefix: values.batch_prefix.trim().toUpperCase(),
        parent_material_id: values.parent_material_id?.trim() || null,
        is_active: values.is_active,
        updated_by: user?.id ?? null,
        updated_at: new Date().toISOString(),
      };
      const { data, error } = await supabase
        .from("materials")
        .update(payload)
        .eq("id", id)
        .select("*")
        .single();
      if (error) throw error;
      return data as Material;
    },
    onSuccess: async (_data, vars) => {
      await queryClient.invalidateQueries({ queryKey: ["materials"] });
      await queryClient.invalidateQueries({ queryKey: ["materials", vars.id] });
      toast.success("Material updated");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to update material"),
  });
}

export function useDeleteMaterial() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("materials").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["materials"] });
      toast.success("Material deleted");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to delete material"),
  });
}

export function useUpsertGrade() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, values }: { id?: string; values: GradeFormValues }) => {
      const payload = {
        name: values.name.trim().toUpperCase(),
        description: text(values.description),
        available_processes: text(values.available_processes),
        is_active: values.is_active,
        updated_at: new Date().toISOString(),
      };
      if (id) {
        const { data, error } = await supabase
          .from("material_grades")
          .update(payload)
          .eq("id", id)
          .select("*")
          .single();
        if (error) throw error;
        return data as MaterialGrade;
      }
      const { data, error } = await supabase.from("material_grades").insert(payload).select("*").single();
      if (error) throw error;
      return data as MaterialGrade;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["material_grades"] });
      toast.success("Grade saved");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to save grade"),
  });
}

export function useDeleteGrade() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("material_grades").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["material_grades"] });
      toast.success("Grade deleted");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to delete grade"),
  });
}

export function useUpsertShapeSize() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, values }: { id?: string; values: ShapeSizeFormValues }) => {
      const payload = {
        shape: values.shape,
        size_label: values.size_label.trim(),
        od: numOrNull(values.od),
        thickness: numOrNull(values.thickness),
        width: numOrNull(values.width),
        length: numOrNull(values.length),
        applicable_material_id: values.applicable_material_id,
        mother_size_id: text(values.mother_size_id),
        description: text(values.description),
        is_active: values.is_active,
        updated_at: new Date().toISOString(),
      };
      if (id) {
        const { data, error } = await supabase
          .from("shape_sizes")
          .update(payload)
          .eq("id", id)
          .select("*")
          .single();
        if (error) throw error;
        return data as ShapeSize;
      }
      const { data, error } = await supabase.from("shape_sizes").insert(payload).select("*").single();
      if (error) throw error;
      return data as ShapeSize;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["shape_sizes"] });
      toast.success("Shape / size saved");
    },
    onError: (error: Error) => {
      const msg = error.message || "Failed to save shape/size";
      if (/shape_sizes_material_shape_label|duplicate key/i.test(msg)) {
        toast.error(
          "Another size already uses this shape + label + OD. Change the label or OD.",
        );
        return;
      }
      toast.error(msg);
    },
  });
}

export function useDeleteShapeSize() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("shape_sizes").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["shape_sizes"] });
      toast.success("Shape / size deleted");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to delete shape/size"),
  });
}

export function useUpsertUom() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, values }: { id?: string; values: UomFormValues }) => {
      const payload = {
        code: values.code.trim().toUpperCase(),
        name: values.name.trim(),
        is_active: values.is_active,
        updated_at: new Date().toISOString(),
      };
      if (id) {
        const { data, error } = await supabase
          .from("uoms")
          .update(payload)
          .eq("id", id)
          .select("*")
          .single();
        if (error) throw error;
        return data as Uom;
      }
      const { data, error } = await supabase.from("uoms").insert(payload).select("*").single();
      if (error) throw error;
      return data as Uom;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["uoms"] });
      toast.success("UOM saved");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to save UOM"),
  });
}

export function useDeleteUom() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("uoms").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["uoms"] });
      toast.success("UOM deleted");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to delete UOM"),
  });
}
